import { ApiErrorPresenterService } from 'src/app/core/services/api-error-presenter.service';
import {
  Company,
  Dashboard,
  DataObject,
  Feed,
  Post,
  User,
  Widget,
  Workspace,
} from '../models/core.models';
import { PresentationNormalizerService } from '../presentation-normalizer.service';
import { DialogCloseEvent, DialogService } from '@sui/dialog';
import { EditorClose } from 'src/app/shared/utils/editor-close.utils';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { NomObjectClass, NomObjectTypes } from '../constants';
import { ShareMode } from '../../objects/dialogs/share-dialog/share-dialog.component';
import { Store } from '@ngrx/store';
import {
  catchError,
  finalize,
  firstValueFrom,
  forkJoin,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { selectCurrentObject } from '../state';

import { AlertEditorDialogComponent } from '../../alerts/dialogs/alert-editor-dialog/alert-editor-dialog.component';
import { AppState } from '../state/app.state';
import { CompanyApiService } from 'src/app/api/services/company-api.service';
import { DashboardEditorDialogComponent } from '../../dashboard/dialogs/dashboard-editor-dialog/dashboard-editor-dialog.component';
import { EditorCompanyDialogComponent } from 'src/app/admin/dialogs/editor-company-dialog/editor-company-dialog.component';
import { EditorUserDialogComponent } from '../../shared/components/editor-user-dialog/editor-user-dialog.component';
import { UtilsDataObjectApiService } from 'src/app/api/services/utils-data-object-api.service';
import { OutputSettingsComponent } from '../../shared/components/output-settings/output-settings.component';
import { PostRelatedDialogComponent } from '../../feed/dialogs/post-related-dialog/post-related-dialog.component';
import { realPostId } from 'src/app/shared/utils/shadow-post.utils';
import { PostSentimentDialogComponent } from '../../feed/dialogs/post-sentiment-dialog/post-sentiment-dialog.component';
import { QueryEditorDialogComponent } from '../../shared/components/query-editor-dialog/query-editor-dialog.component';
import { ShareDialogComponent } from '../../objects/dialogs/share-dialog/share-dialog.component';
import { ToastService } from '@services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { UserFacadeService } from '@services/user-facade.service';
import { UserApiService } from 'src/app/api/services/user-api.service';
import { WidgetApiService } from 'src/app/api/services/widget-api.service';
import { WidgetViewStateService } from './widget-view-state.service';
import { WidgetSettingsDialogComponent } from '../../widgets/dialogs/widget-settings-dialog/widget-settings-dialog.component';
import { WorkspaceEditorDialogComponent } from '../../workspace/dialogs/workspace-editor-dialog/workspace-editor-dialog.component';
import { injectFetchOutputSettingsRecord } from 'src/app/api/queries/output-settings.queries';
import { OutputSettings } from '../models/output-settings.models';

import { DataObjectRef } from '../models/object-ref.models';

// Kept for existing importers - the interface now lives in object-ref.models.
export { DataObjectRef } from '../models/object-ref.models';

@Injectable({
  providedIn: 'root',
})
export class ObjectEditorService {
  protected utilsDataObjectApi = inject(UtilsDataObjectApiService);
  protected userFacade = inject(UserFacadeService);
  protected toast = inject(ToastService);
  protected translateService = inject(TranslateService);
  private readonly presenter = inject(ApiErrorPresenterService);
  private dialogService = inject(DialogService);
  private router = inject(Router);
  private widgetViewState = inject(WidgetViewStateService);
  private fetchOutputSettingsRecord = injectFetchOutputSettingsRecord();

  private isLoading = false;

  constructor(
    private store: Store<AppState>,
    private widgetApi: WidgetApiService,
    private companyApi: CompanyApiService,
    private userApi: UserApiService,
  ) {}

  // * Opens a dialog to edit the specified feed.
  // * @param feed - The feed to be edited.
  // * @param tabId - if provided open the dialog with the specified tab selected.
  private editFeed(feed: Feed, tabId?: string): EditorClose {
    if (
      this.userFacade.hasAllObjectPermissionsSync(feed, ['can_edit']) === false
    ) {
      this.toast.showError(
        this.translateService.instant('toast.error.no-permission-edit-feed'),
      );
      return Promise.resolve(null);
    }

    const dialogRef = this.dialogService.openTabsDialog(
      QueryEditorDialogComponent,
      {
        data: {
          feed,
          tabId,
        },
        closeOnClick: false,
        closeOnEscape: true,
        width: '80vw',
        height: '80vh',
      },
    );

    return firstValueFrom(dialogRef.closed, { defaultValue: null });
  }

  private editWorkspace(workspace: Workspace): EditorClose {
    if (
      this.userFacade.hasAllObjectPermissionsSync(workspace, ['can_edit']) ===
      false
    ) {
      this.toast.showError(
        this.translateService.instant(
          'toast.error.no-permission-edit-workspace',
        ),
      );
      return Promise.resolve(null);
    }

    const dialogRef = this.dialogService.openTabsDialog(
      WorkspaceEditorDialogComponent,
      {
        data: {
          workspace: workspace,
          validateFormsOnInit: true,
        },
        closeOnClick: false,
        closeOnEscape: true,
        height: '50vh',
        minHeight: 800,
        maxHeight: 1020,
      },
    );

    return firstValueFrom(dialogRef.closed, { defaultValue: null });
  }

  private editDashboard(dashboard: Dashboard): EditorClose {
    if (
      this.userFacade.hasAllObjectPermissionsSync(dashboard, ['can_edit']) ===
      false
    ) {
      this.toast.showError(
        this.translateService.instant(
          'toast.error.no-permission-edit-dashboard',
        ),
      );
      return Promise.resolve(null);
    }

    const dialogRef = this.dialogService.openTabsDialog(
      DashboardEditorDialogComponent,
      {
        data: {
          dashboard: dashboard,
        },
        closeOnClick: false,
        closeOnEscape: true,
        height: '50vh',
        minHeight: 800,
        maxHeight: 1020,
      },
    );

    return firstValueFrom(dialogRef.closed, { defaultValue: null });
  }

  editRelated(post, feedId, feedTypeId) {
    const dialogRef = this.dialogService.openTabsDialog(
      PostRelatedDialogComponent,
      {
        data: {
          post: post,
          feedId: feedId,
          feedTypeId: feedTypeId,
        },
        closeOnClick: false,
        closeOnEscape: true,
        width: '50vw',
        height: '50vh',
        minWidth: 1536,
        minHeight: 864,
        maxWidth: 1020,
        maxHeight: 1020,
      },
    );

    dialogRef.closed.subscribe((result) => {
      console.log(
        '%c Dialog closed with result:',
        'color: green; font-weight: bold; ',
        result,
      );
    });
  }

  editSentiment(feedId: string, id, sentiment_id, sentiment_value_auto) {
    const dialogRef = this.dialogService.openTabsDialog(
      PostSentimentDialogComponent,
      {
        data: {
          feedId: feedId,
          postId: id,
          inputSentimentId: sentiment_id,
          sentimentValue: sentiment_value_auto,
        },
        closeOnClick: false,
        closeOnEscape: true,
        height: '30vh',
        // minWidth: 800,
        // minHeight: 800,
        // maxWidth: 1020,
        // maxHeight: 1020,
      },
    );

    dialogRef.closed.subscribe((result) => {
      console.log(
        '%c Dialog closed with result:',
        'color: green; font-weight: bold; ',
        result,
      );
    });
  }

  /**
   * Opens the widget editor on a fresh copy of the widget. A failed fetch shows
   * the error and opens nothing - never the stale card copy.
   */
  editWidget(widgetId: string, tab?: string): EditorClose {
    return firstValueFrom(
      this.widgetApi.getWidget(widgetId).pipe(
        take(1),
        switchMap((freshWidget) =>
          this.openWidgetDialog(
            {
              ...freshWidget,
              // * the rendered Highcharts config (the "debug chart JSON" tab) is never
              // * persisted, so it comes from `WidgetViewStateService`
              chart_options: this.widgetViewState.getChartOptions(widgetId),
            },
            tab,
          ),
        ),
        catchError((error) => {
          console.error('Failed to fetch widget', error);
          this.presenter.present(error, {
            fallback: this.translateService.instant(
              'toast.error.widget-load-failed',
            ),
          });
          return of(null);
        }),
      ),
      { defaultValue: null },
    );
  }

  private openWidgetDialog(widget: Widget, tab?: string): EditorClose {
    const dialogRef = this.dialogService.openTabsDialog(
      WidgetSettingsDialogComponent,
      {
        data: {
          widget: widget,
          tab: tab ? tab : null,
          validateFormsOnInit: true,
        },
        closeOnClick: false,
        closeOnEscape: true,
        height: '85vh',
        minHeight: 864,
        maxHeight: 1080,
      },
    );

    return firstValueFrom(dialogRef.closed, { defaultValue: null });
  }

  /**
   * Mass update opens on the widgets' current server state (one fetch per id),
   * never on the dashboard's in-memory copies; a failed fetch opens nothing.
   */
  openMassUpdateWidgetDialog(widgetIds: string[], tab?: string): EditorClose {
    if (widgetIds.length === 0) return Promise.resolve(null);

    return firstValueFrom(
      forkJoin(widgetIds.map((id) => this.widgetApi.getWidget(id))).pipe(
        switchMap(
          (widgets) =>
            this.dialogService.openTabsDialog(WidgetSettingsDialogComponent, {
              data: {
                tab: tab ? tab : null,
                validateFormsOnInit: false,
                massUpdate: true,
                widgets,
              },
              closeOnClick: false,
              closeOnEscape: true,
              // ~half size, still responsive
              width: '60vw',
              height: '60vh',
              // 16:9 standard constraints
              minWidth: 960,
              minHeight: 540,
              maxWidth: 1280,
              maxHeight: 720,
            }).closed,
        ),
        catchError((error) => {
          console.error('Failed to fetch widgets for mass update', error);
          this.presenter.present(error, {
            fallback: this.translateService.instant(
              'toast.error.widget-load-failed',
            ),
          });
          return of(null);
        }),
      ),
      { defaultValue: null },
    );
  }

  /**
   * Edits the output settings for a given outputSettingsId.
   * @param outputSettingsId - The ID of the output settings to edit.
   */
  async editOutputSettings(
    outputSettingsId: number,
    objectId: string,
    objectClassId: number,
  ): EditorClose {
    // * services fetch through the QueryClient, never a hook - see api-layer.md.
    // * A failed fetch opens no dialog and reports nothing, by design.
    let outputSettings: OutputSettings;

    try {
      outputSettings = await this.fetchOutputSettingsRecord({
        outputSettingsId,
        objectId,
        objectClassId,
      });
    } catch {
      return null;
    }

    if (!outputSettings) {
      return null;
    }

    // The admin grid (WAP-1638) opens a company's named list with no current
    // object on screen; the row's own object then names the dialog scope.
    const currentObject = await firstValueFrom(
      this.store.select(selectCurrentObject).pipe(take(1)),
    );

    const dialogRef = this.dialogService.openTabsDialog(
      OutputSettingsComponent,
      {
        data: {
          outputSettings,
          objectId: currentObject?.id ?? objectId,
          objectClassId: currentObject?.object_class_id ?? objectClassId,
        },
        closeOnClick: false,
        closeOnEscape: true,
        height: '75%',
        minHeight: 810,
        maxHeight: 900,
      },
    );

    return firstValueFrom(dialogRef.closed, { defaultValue: null });
  }

  /**
   * Checks the feed and opens a popup if the feed is fully loaded.
   * If the feed is not fully loaded, it dispatches an action to load the feed and waits for it to be loaded.
   * If there is an error loading the feed, it handles the error appropriately.
   * If tabId is provided, load the dialog with that tab open
   * @param feedId The ID of the feed to check and open the popup for.
   */
  loadFeedAndOpenPopup(feedId: string, tabId?: string): EditorClose {
    if (this.isLoading) return Promise.resolve(null);
    this.isLoading = true;

    return firstValueFrom(
      this.utilsDataObjectApi
        .fetchObject(feedId, NomObjectClass.FEED, false)
        .pipe(
          tap({
            next: () => (this.isLoading = false),
            error: () => (this.isLoading = false),
          }),
          switchMap((dataObject: DataObject) =>
            dataObject ? this.editFeed(dataObject as Feed, tabId) : of(null),
          ),
          catchError((err) => {
            console.error('Error fetching feed object', err);
            this.presenter.present(err, {
              fallback: this.translateService.instant(
                'object-editor.toast.feed-could-not-be-loaded',
              ),
            });
            return of(null);
          }),
          finalize(() => {
            this.isLoading = false;
          }),
        ),
      { defaultValue: null },
    );
  }

  loadWorkspaceAndOpenPopup(workspaceId: string): EditorClose {
    return firstValueFrom(
      this.utilsDataObjectApi
        .fetchObject(workspaceId, NomObjectClass.WORKSPACE, false)
        .pipe(
          switchMap((dataObject: DataObject) =>
            dataObject ? this.editWorkspace(dataObject as Workspace) : of(null),
          ),
          catchError((err) => {
            console.error('Error fetching workspace object', err);
            this.presenter.present(err, {
              fallback: this.translateService.instant(
                'object-editor.toast.workspace-could-not-be-loaded',
              ),
            });
            return of(null);
          }),
        ),
      { defaultValue: null },
    );
  }

  loadDashboardAndOpenPopup(dashboardId: string): EditorClose {
    return firstValueFrom(
      this.utilsDataObjectApi
        .fetchObject(dashboardId, NomObjectClass.DASHBOARD, false)
        .pipe(
          switchMap((dataObject: DataObject) => {
            if (!dataObject) return of(null);
            // * Normalize dashboard before passing to dialog
            const normalizer = new PresentationNormalizerService();
            return this.editDashboard(
              normalizer.normalizeDashboard(dataObject as Dashboard),
            );
          }),
          catchError((err) => {
            console.error('Error fetching dashboard object', err);
            this.presenter.present(err, {
              fallback: this.translateService.instant(
                'object-editor.toast.dashboard-could-not-be-loaded',
              ),
            });
            return of(null);
          }),
        ),
      { defaultValue: null },
    );
  }

  sharePost(post: Post, shareParams: any = {}): void {
    this.store
      .select(selectCurrentObject)
      .pipe(take(1))
      .subscribe((currentObject) => {
        const dialogRef = this.dialogService.openTabsDialog(
          ShareDialogComponent,
          {
            data: {
              shareMode: ShareMode.SINGLE,
              objectId: currentObject.object_id,
              objectClassId: currentObject.object_class_id,
              // The post may be a display-only thread "shadow" copy; the backend
              // only knows the real post id.
              postIds: [realPostId(post.id)],
              title: post.title,
              shareParams: shareParams,
            },
            closeOnClick: false,
            closeOnEscape: true,
            height: '60%',
            minHeight: 800,
            maxHeight: 1020,
          },
        );

        dialogRef.closed.subscribe((event: DialogCloseEvent<unknown>) => {
          console.log(
            '%c Dialog closed with event:',
            'color: green; font-weight: bold;',
            event,
          );
        });
      });
  }

  editCompany(companyId: string): EditorClose {
    if (this.isLoading) return Promise.resolve(null);

    this.isLoading = true;

    return firstValueFrom(
      this.companyApi.getCompany(companyId).pipe(
        switchMap((company: Company) => {
          if (!company) return of(null);
          return forkJoin({
            company: of(company),
            companyFeatures: this.companyApi.getCompanyFeatures(company.id),
          });
        }),
        tap({
          next: () => (this.isLoading = false),
          error: () => (this.isLoading = false),
        }),
        switchMap((result) => {
          if (!result) return of(null);
          const { company, companyFeatures } = result;
          return this.dialogService.openTabsDialog(
            EditorCompanyDialogComponent,
            {
              data: { company, companyFeatures },
              closeOnClick: false,
              closeOnEscape: true,
              height: '80%',
              minHeight: 864,
              maxHeight: 1080,
            },
          ).closed;
        }),
        catchError((error) => {
          console.error('Failed to fetch company or features', error);
          this.presenter.present(error, {
            fallback: this.translateService.instant(
              'object-editor.toast.company-could-not-be-loaded',
            ),
          });
          return of(null);
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      ),
      { defaultValue: null },
    );
  }

  editUser(userId: string): EditorClose {
    return firstValueFrom(
      this.userApi.getUser(userId).pipe(
        switchMap((user: User) => {
          // * a 200 with an empty body is "no such user" - nothing to open
          if (!user) return of(null);
          return this.dialogService.openTabsDialog(EditorUserDialogComponent, {
            data: { user },
            closeOnClick: false,
            closeOnEscape: true,
            height: '70vh',
            minHeight: 800,
            maxHeight: 1020,
          }).closed;
        }),
        catchError((error) => {
          console.error('Failed to fetch user', error);
          this.presenter.present(error, {
            fallback: this.translateService.instant(
              'object-editor.toast.user-could-not-be-loaded',
            ),
          });
          return of(null);
        }),
      ),
      { defaultValue: null },
    );
  }

  /**
   * Which canonical object refs (SEN-5207 nested `object` blocks) the shared
   * grid title-click can open: feeds, dashboards and workspaces navigate by
   * URL (feeds/dashboards need the ref's workspace id), dimensions open the
   * feed editor dialog, users/companies/alerts their settings dialogs. Used
   * as the renderer's `clickable` gate so refs without a UI target render as
   * plain text.
   */
  canOpenDataObjectRef(ref: DataObjectRef | null): boolean {
    if (!ref?.id) {
      return false;
    }
    switch (ref.object_class_id) {
      case NomObjectClass.FEED:
        // Dimensions are FEED-class objects with the dimension type - they
        // have no URL and open in the feed editor dialog instead.
        return (
          ref.object_type_id === NomObjectTypes.SEARCH_DIMENSION ||
          !!ref.workspace?.id
        );
      case NomObjectClass.DASHBOARD:
        return !!ref.workspace?.id;
      case NomObjectClass.RULE:
        return !!ref.connector?.id;
      case NomObjectClass.WORKSPACE:
      case NomObjectClass.COMPANY:
      case NomObjectClass.USER:
      case NomObjectClass.ALERT:
        return true;
      default:
        // Deliberately unhandled classes render as plain text. POST stays
        // here by decision - posts have no standalone editor or settings
        // component to open.
        return false;
    }
  }

  /**
   * Open the object behind a canonical ref - see canOpenDataObjectRef(). Editors
   * hand back their close handle so the calling grid can refresh the row; URL
   * navigations resolve to null at once.
   */
  openDataObjectRef(
    ref: DataObjectRef | null,
    newTab: boolean = true,
  ): EditorClose {
    if (!this.canOpenDataObjectRef(ref)) {
      return Promise.resolve(null);
    }

    switch (ref.object_class_id) {
      case NomObjectClass.FEED:
        if (ref.object_type_id === NomObjectTypes.SEARCH_DIMENSION) {
          return this.loadFeedAndOpenPopup(ref.id);
        }
        this.openAnalyticsUrl(
          ['workspace', ref.workspace.id, 'feed', ref.id],
          newTab,
        );
        break;
      case NomObjectClass.DASHBOARD:
        this.openAnalyticsUrl(
          ['workspace', ref.workspace.id, 'dashboard', ref.id],
          newTab,
        );
        break;
      case NomObjectClass.WORKSPACE:
        this.openAnalyticsUrl(['workspace', ref.id], newTab);
        break;
      case NomObjectClass.RULE:
        this.openAnalyticsUrl(
          ['connectors', ref.connector.id, 'rule', ref.id],
          newTab,
        );
        break;
      case NomObjectClass.COMPANY:
        return this.editCompany(ref.id);
      case NomObjectClass.USER:
        return this.editUser(ref.id);
      case NomObjectClass.ALERT:
        return this.editAlert(ref.id);
    }

    return Promise.resolve(null);
  }

  private openAnalyticsUrl(segments: string[], newTab: boolean): void {
    const urlTree = this.router.createUrlTree([
      environment.baseUrlEnvironmentSuffix,
      'analytics',
      ...segments,
    ]);
    if (newTab) {
      window.open(this.router.serializeUrl(urlTree), '_blank');
    } else {
      this.router.navigateByUrl(urlTree);
    }
  }

  editAlert(alertId: string): EditorClose {
    if (this.isLoading) return Promise.resolve(null);

    this.isLoading = true;

    return firstValueFrom(
      this.utilsDataObjectApi
        .fetchObject(alertId, NomObjectClass.ALERT, false)
        .pipe(
          tap({
            next: () => (this.isLoading = false),
            error: () => (this.isLoading = false),
          }),
          switchMap((alert: any) => {
            if (!alert) return of(null);
            return this.dialogService.openTabsDialog(
              AlertEditorDialogComponent,
              {
                data: { alert },
                closeOnClick: false,
                closeOnEscape: true,
                height: '50%',
                minHeight: 800,
                maxHeight: 1020,
              },
            ).closed;
          }),
          catchError((error) => {
            console.error('Failed to fetch alert', error);
            this.presenter.present(error, {
              fallback: this.translateService.instant(
                'object-editor.toast.alert-could-not-be-loaded',
              ),
            });
            return of(null);
          }),
          finalize(() => {
            this.isLoading = false;
          }),
        ),
      { defaultValue: null },
    );
  }
}
